/**
 * Overview Tab Component
 * 
 * Displays user overview information in a clean grid layout.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, UserCircle, User as UserIcon, Mail, Phone, MessageCircle, CreditCard, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { User } from "@/services/authService";
import { formatStatusKeanggotaan } from "../utils/statusUtils";
import { cn } from "@/lib/utils";

interface OverviewTabProps {
  user: User;
}

function formatPhoneForWhatsApp(phone: string): string {
  let cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("0")) cleaned = "62" + cleaned.substring(1);
  else if (!cleaned.startsWith("62")) cleaned = "62" + cleaned;
  return cleaned;
}

function Field({ label, value, icon, action, className }: { label: string; value: string; icon?: React.ReactNode; action?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("p-4", className)}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">{value}</span>
        {action}
      </div>
    </div>
  );
}

export function OverviewTab({ user }: OverviewTabProps) {
  const whatsappUrl = user.no_hp
    ? `https://wa.me/${formatPhoneForWhatsApp(user.no_hp)}`
    : null;

  const jabatan =
    user.jabatanAssignments && user.jabatanAssignments.length > 0
      ? user.jabatanAssignments
          .filter((a) => a.isActive)
          .map((a) => a.jabatan.name)
          .join(", ") || "-"
      : "Anggota";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ringkasan</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Status badges */}
        <div className="flex flex-wrap gap-2 mb-6">
          {user.status_callsign && (
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" />
              {user.status_callsign}
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <UserCircle className="h-3 w-3" />
            {formatStatusKeanggotaan(user.status_keanggotaan)}
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Shield className="h-3 w-3" />
            {jabatan}
          </Badge>
        </div>

        {/* Info grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border border-border rounded-lg overflow-hidden">
          <Field label="Nama Lengkap" value={user.name || "-"} icon={<UserIcon className="h-3.5 w-3.5" />} className="border-b sm:border-r" />
          <Field label="Callsign" value={user.callsign || "-"} icon={<UserCircle className="h-3.5 w-3.5" />} className="border-b lg:border-r" />
          <Field label="Email" value={user.email || "-"} icon={<Mail className="h-3.5 w-3.5" />} className="border-b lg:border-r" />
          <Field
            label="No. HP"
            value={user.no_hp || "-"}
            icon={<Phone className="h-3.5 w-3.5" />}
            className="sm:border-r"
            action={
              whatsappUrl ? (
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20" asChild>
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" title="WhatsApp">
                    <MessageCircle className="h-3 w-3" />
                  </a>
                </Button>
              ) : undefined
            }
          />
          <Field
            label="Jenis Kelamin"
            value={user.jenis_kelamin === "L" ? "Laki-laki" : user.jenis_kelamin === "P" ? "Perempuan" : user.jenis_kelamin || "-"}
            icon={<UserIcon className="h-3.5 w-3.5" />}
          />
          <Field
            label="No. KTP"
            value={
              user.ktp
                ? user.ktp.length >= 4
                  ? `${user.ktp.substring(0, 2)}****${user.ktp.substring(user.ktp.length - 2)}`
                  : "****"
                : "-"
            }
            className="border-t sm:border-l"
            icon={<CreditCard className="h-3.5 w-3.5" />}
            action={
              user.ktp && user.isValidKTP !== undefined && user.isValidKTP !== null ? (
                user.isValidKTP ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" title="KTP Valid" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" title="KTP Tidak Valid" />
                )
              ) : undefined
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
